import { FC, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { GroupBase, OptionsOrGroups } from "react-select";

import HubInputField from "src/components/inputType";
import useFetch from "src/hooks/useFetch";

import { APIEndpoints } from "src/types/apiTypes";
import { ZilrettaOnStepTwoProps } from "./types";
import { PatientDropdownResponse, PracticeDropdownResponse, ProviderDropdownResponse } from "src/components/caseStepTwo/types";
import { resetDoctorAndPatientDetails } from "src/helpers/addCase";
import usePatientDetailFunc from "src/hooks/usePatientDetailFunc";




const ZilrettaOnStepTwo: FC<ZilrettaOnStepTwoProps> = ({ control, setValue, watch, getValues, trigger }): JSX.Element => {
  const [firstPracticeId] = useState(getValues("doctor_attributes.practice_id"));

  const practiceId = watch("doctor_attributes.practice_id");
  const doctorId = watch("doctor_attributes.global_doctor_id");
  const globalPatientId = watch("patient_attributes.global_patient_id");

  usePatientDetailFunc(globalPatientId, setValue, trigger, getValues)





  /**
   * @getPracticeDropdown api to get practice based listing
   * @first initialize the useFetch() hook
   * @second trigger practice listing api to get listing
   */
  const {data: practiceDropdownApiRes } = useFetch<PracticeDropdownResponse>(APIEndpoints.PRACTICE_DROPDOWN, {});




  const { data: globalDoctorsApiResponse, mutate: getGlobalDoctorsListing } =
    useFetch<ProviderDropdownResponse>(APIEndpoints.PROVIDER_DROPDOWN);

  /** @second */
  useEffect(() => {
    if (practiceId && practiceDropdownApiRes) {
      if (practiceId !== firstPracticeId)
        resetDoctorAndPatientDetails(setValue);

      const globalDoctorParams = {
        practice_id: practiceId,
      };

      getGlobalDoctorsListing(null, null, globalDoctorParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, practiceDropdownApiRes]);





 /**
   * @getPatientListingByDoctorId api to get patient listing from doctor id
   * @first initialize the useFetch() hook
   * @second calling patient api using selected doctor id
   * @third patient api success handler
   */
  const { error: patientListingApiError, data: patientListingApiResponse, mutate: getPatientListing } =
    useFetch<PatientDropdownResponse>(APIEndpoints.PATIENT_DROPDOWN);

  /** @second */
  useEffect(() => {
  if (practiceId && doctorId && globalDoctorsApiResponse) {
    const patientQueryParams = {
      practice_id: practiceId,
    };

    getPatientListing(null, null, patientQueryParams);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, globalDoctorsApiResponse]);

  /** @third */
  useEffect(() => {
    if (patientListingApiResponse || patientListingApiError)
      setValue("patient_attributes.global_patient_id", globalPatientId || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientListingApiResponse, patientListingApiError]);




  //:- Filling missing data into parent object
  useEffect(() => {
    if (practiceId) {
      const practiceData = practiceDropdownApiRes?.data.find((value) => value.id === practiceId);
      if (practiceData) setValue("practice_name", `${practiceData.name}${practiceData.address ? ` - ${practiceData.address.full_address}` : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  useEffect(() => {
    if (doctorId) {
      const doctorObj = globalDoctorsApiResponse?.data.find((value) => value.id === doctorId);
      if (doctorObj) {
        setValue("doctor_fullname", doctorObj.fullname);
        setValue("doctor_firstlastname", doctorObj.first_last_name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);


  return (
    <div className="card-body">
      <Controller
        name="doctor_attributes.practice_id"
        control={control}
        defaultValue={0}
        render={({ field, fieldState: { error } }) => (
          <HubInputField
            inputId="doctor_attributes.practice_id"
            inputLabel="Submitting Practice"
            inputPlaceholder="Select a Practice..."
            isRequired={true}
            isRowBased={true}
            field={field}
            error={error}
            isDropdown={true}
            isSearchable={false}
            dropdownData={
              (practiceDropdownApiRes && practiceDropdownApiRes.data && practiceDropdownApiRes.data.length > 0 ?
                practiceDropdownApiRes.data.map((practice) => ({ label:  practice.address ? `${practice.name.charAt(0).toUpperCase() + practice.name.slice(1)} - ${practice.address.full_address}` : practice.name , value: practice.id }))
                : []
              ) as unknown as OptionsOrGroups<string, GroupBase<string>>
            }
          />
        )}
      />
      {practiceId ? (
        <Controller
          name="doctor_attributes.global_doctor_id"
          control={control}
          defaultValue={0}
          render={({ field, fieldState: { error } }) => (
            <HubInputField
              inputId="doctor_attributes.global_doctor_id"
              inputLabel="Treating Provider"
              inputPlaceholder="Select a Doctor..."
              isRequired={true}
              isRowBased={true}
              field={field}
              error={error}
              isDropdown={true}
              isSearchable={true}
              dropdownData={
                (globalDoctorsApiResponse && globalDoctorsApiResponse.data && globalDoctorsApiResponse.data.length > 0 ?
                  globalDoctorsApiResponse.data.map((provider) => ({ value: provider.id, label: provider.fullname }))
                  : []
                ) as unknown as OptionsOrGroups<string, GroupBase<string>>
              }
            />
          )}
        />
      ) : null}
      {doctorId ? (
        <Controller
          name="patient_attributes.global_patient_id"
          control={control}
          defaultValue={0}
          render={({ field, fieldState: { error } }) => (
            <div className="row mb-2">
              <label htmlFor="patient_attributes.global_patient_id" className="form-label col-lg-2 col-md-4 col-12 p-2">
                Select a Patient *
              </label>
              <div 
                className="col-lg-10 col-md-8 col-12"
                title={(
                  patientListingApiResponse && patientListingApiResponse.data && field.value ?
                    patientListingApiResponse.data.find((item) => item.id === field.value)?.fullname
                    : "(New Patient)"
                )}
                data-bs-toggle="tooltip"
                data-bs-placement="bottom"
              >
                <HubInputField
                  inputId="patient_attributes.global_patient_id"
                  inputPlaceholder="(new patient)..."
                  isRequired={true}
                  isRowBased={true}
                  field={field}
                  error={error}
                  isDropdown={true}
                  isSearchable={true}
                  dropdownData={
                    (patientListingApiResponse && patientListingApiResponse.data && patientListingApiResponse.data.length > 0 ?
                      [{ value: 0, label: `(New Patient)` }, ...patientListingApiResponse.data.map((patient) => ({ value: patient.id, label: patient.fullname }))]
                      : [{ value: 0, label: `(New Patient)` }]
                    ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                  }
                />
              </div>
            </div>
          )}
        />
      ) : null}
    </div>
  )
}

export default ZilrettaOnStepTwo
